import React, { useState, useEffect } from 'react';
import { UserProvider, useUser } from './contexts/UserContext';
import {
  auth,
  signOut,
  db,
  collection,
  getDocs
} from './firebase';
import Login from './components/Auth/Login';
import UnclaimedDevices from './components/UnclaimedDevices';
import Portfolios from './components/Portfolios';
import AccountSettings from './components/AccountSettings';
import UserManagement from './components/UserManagement';
import Fleet from './components/Fleet';
import TermsOfService from './components/TermsOfService';

function AppContent() {
  const { user, userProfile, loading, isSuperAdmin, isSystemAdmin } = useUser();
  const [showUnclaimedDevices, setShowUnclaimedDevices] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showFleet, setShowFleet] = useState(false);
  // Bumped when the Portfolios header button is clicked, so the Portfolios
  // component can jump back to the all-portfolios overview.
  const [portfoliosHomeTick, setPortfoliosHomeTick] = useState(0);
  const [showTerms, setShowTerms] = useState(false);
  const [portfolios, setPortfolios] = useState([]);

  // Fetch portfolios for unclaimed devices dropdown
  const fetchPortfolios = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'customer_portfolios'));
      const portfolioList = [];
      snapshot.forEach((doc) => {
        portfolioList.push({ id: doc.id, ...doc.data() });
      });
      setPortfolios(portfolioList);
    } catch (error) {
      console.error('Error fetching portfolios:', error);
    }
  };

  // Fetch portfolios on mount
  useEffect(() => {
    if (user) {
      fetchPortfolios();
    }
  }, [user]);

  const handleLogin = () => {
    // UserContext handles the user state
  };

  const handleLogout = async () => {
    await signOut(auth);
    setShowUnclaimedDevices(false);
    setShowAccountSettings(false);
    setShowUserManagement(false);
    setShowFleet(false);
  };

  const resetViews = () => {
    setShowUnclaimedDevices(false);
    setShowAccountSettings(false);
    setShowUserManagement(false);
    setShowFleet(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="app loading-screen">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  // Main app - Portfolios is the default view
  return (
    <div className="app">
      <header className="header">
        <div className="header-left">
          <h1 onClick={resetViews}>
            TempSensor
          </h1>
          {userProfile && (
            <span className="user-info">
              {userProfile.name || userProfile.email}
              {isSuperAdmin() && <span className="role-badge super">Super Admin</span>}
              {userProfile.role === 'admin' && <span className="role-badge admin">Admin</span>}
            </span>
          )}
        </div>
        <div className="header-right">
          <button
            className={`header-btn ${!showUnclaimedDevices && !showAccountSettings && !showUserManagement && !showFleet ? 'active' : ''}`}
            onClick={() => { resetViews(); setPortfoliosHomeTick((t) => t + 1); }}
          >
            Portfolios
          </button>
          {isSystemAdmin() && (
            <button
              className={`header-btn ${showFleet ? 'active' : ''}`}
              onClick={() => { resetViews(); setShowFleet(true); }}
            >
              Fleet
            </button>
          )}
          {isSystemAdmin() && (
            <button
              className={`header-btn ${showUnclaimedDevices ? 'active' : ''}`}
              onClick={() => { resetViews(); setShowUnclaimedDevices(true); }}
            >
              Unclaimed Devices
            </button>
          )}
          {isSystemAdmin() && (
            <button
              className={`header-btn ${showUserManagement ? 'active' : ''}`}
              onClick={() => { resetViews(); setShowUserManagement(true); }}
            >
              Users
            </button>
          )}
          <button
            className={`header-btn ${showAccountSettings ? 'active' : ''}`}
            onClick={() => { resetViews(); setShowAccountSettings(true); }}
          >
            Account
          </button>
          <button className="header-btn logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <main className="main">
        {showFleet ? (
          <Fleet onBack={resetViews} />
        ) : showUserManagement ? (
          <UserManagement onClose={resetViews} />
        ) : showAccountSettings ? (
          <AccountSettings onClose={resetViews} />
        ) : showUnclaimedDevices ? (
          <UnclaimedDevices
            portfolios={portfolios}
            onBack={resetViews}
            onPortfolioCreated={fetchPortfolios}
          />
        ) : (
          <Portfolios homeTick={portfoliosHomeTick} />
        )}
      </main>

      <footer className="app-footer">
        <button className="footer-link" onClick={() => setShowTerms(true)}>
          Terms and Conditions of Service
        </button>
      </footer>

      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}

      <style>{`
        .user-info {
          font-size: 14px;
          color: #888;
          margin-left: 20px;
        }
        .role-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          margin-left: 8px;
          font-weight: 600;
        }
        .role-badge.super {
          background: #e74c3c;
          color: #fff;
        }
        .role-badge.admin {
          background: #9b59b6;
          color: #fff;
        }
        .app-footer {
          text-align: center;
          padding: 16px;
        }
        .footer-link {
          background: none;
          border: none;
          padding: 0;
          color: #556;
          font-size: 12px;
          cursor: pointer;
        }
        .footer-link:hover {
          color: #8fa3c8;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <UserProvider>
      <AppContent />
    </UserProvider>
  );
}

export default App;
